import React, { useEffect, useState } from 'react';
import QRCode from 'qrcode';
import api from '../../services/api.js';

/**
 * Signed QR stickers for a charger, one per connector. A driver (or a walk-up guest) scans the sticker
 * to start a charge on exactly that connector. The link is signed by the server; the image is drawn in
 * the browser. Print them and stick each one next to its connector.
 */
export default function ChargerQrCodes({ chargeBoxId, siteName, connectors }) {
  const [codes, setCodes] = useState(null); // [{ index, type, maxPowerKw, url, image }]
  const [error, setError] = useState('');
  const [copied, setCopied] = useState(null);
  const key = `${chargeBoxId}:${connectors.map((c) => c.index).join(',')}`;

  useEffect(() => {
    let live = true;
    setCodes(null);
    setError('');
    Promise.all(connectors.map(async (c) => {
      const { url } = await api.signChargerQr(chargeBoxId, c.index);
      const image = await QRCode.toDataURL(url, { errorCorrectionLevel: 'M', margin: 2, width: 320 });
      return { index: c.index, type: c.type, maxPowerKw: c.maxPowerKw, url, image };
    }))
      .then((list) => live && setCodes(list))
      .catch((e) => live && setError(e.status === 403 ? 'Your role cannot generate charger QR codes.' : e.message || 'Could not generate the QR codes.'));
    return () => { live = false; };
    // The connector list is re-created on every poll; `key` captures what actually matters.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [key]);

  const copy = async (c) => {
    try {
      await navigator.clipboard.writeText(c.url);
      setCopied(c.index);
      setTimeout(() => setCopied(null), 1500);
    } catch { /* clipboard unavailable: the link text is selectable */ }
  };

  // Print only the stickers: the page hides everything else while this class is on <body>.
  const print = () => {
    document.body.classList.add('qr-printing');
    const done = () => { document.body.classList.remove('qr-printing'); window.removeEventListener('afterprint', done); };
    window.addEventListener('afterprint', done);
    window.print();
    setTimeout(done, 1000);
  };

  return (
    <div className="card p-3 shadow-sm border mb-3" id="charger-qr-card">
      <div className="d-flex flex-wrap justify-content-between align-items-center gap-2 mb-2">
        <h3 className="h6 fw-bold mb-0"><i className="fa fa-qrcode me-2" aria-hidden="true" />Charger QR codes</h3>
        <button type="button" className="btn btn-sm btn-outline-primary" id="btn-print-qr" disabled={!codes} onClick={print}><i className="fa fa-print me-1" aria-hidden="true" />Print</button>
      </div>
      <p className="small text-muted mb-2">Stick each code next to its connector. Drivers scan it in the app to start charging; visitors without an account are taken to walk-up checkout.</p>
      {error && <div className="small text-danger" role="alert">{error}</div>}
      {!codes && !error && <div className="small text-muted">Generating…</div>}
      {codes && codes.length === 0 && <div className="small text-muted">This charger has no connectors registered, so there is nothing to print.</div>}
      {codes && (
        <div className="qr-print-area d-flex flex-wrap gap-3">
          {codes.map((c) => (
            <div key={c.index} className="qr-sticker text-center" data-connector={c.index}>
              <img src={c.image} alt={`QR code for ${chargeBoxId} connector ${c.index}`} width="160" height="160" />
              <div className="fw-bold small">{chargeBoxId} · #{c.index}</div>
              <div className="text-muted small">{c.type} {c.maxPowerKw} kW{siteName ? ` · ${siteName}` : ''}</div>
              <div className="qr-sticker-url">{c.url}</div>
              <button type="button" className="btn btn-sm btn-link p-0 qr-no-print" onClick={() => copy(c)}>{copied === c.index ? 'Copied' : 'Copy link'}</button>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
